/**
 * Copyright © 2026 Adnuntius AS.
 */

export interface PreviewLanguageOption {
  value: string;
  label: string;
}

export const PREVIEW_LANGUAGE_OPTIONS: PreviewLanguageOption[] = [
  {value: 'browser', label: 'Use browser language (default)'},
  {value: 'no', label: 'Norwegian'},
  {value: 'sv', label: 'Swedish'},
  {value: 'da', label: 'Danish'},
  {value: 'fi', label: 'Finnish'},
  {value: 'de', label: 'German'}
];

export function isPoliticalPreviewCreative(creative: any): boolean {
  if (!creative) {
    return false;
  }

  const layout = creative.layout || {};
  const lineItem = creative.lineItem || {};
  const axProduct = lineItem.axProduct || {};

  return !!(
    layout.political ||
    creative.political ||
    creative.isPoliticalPreview ||
    creative.productType === 'POLITICAL' ||
    axProduct.type === 'POLITICAL' ||
    (lineItem.election && lineItem.election.id) ||
    (creative.election && creative.election.id)
  );
}

export function isVastRelatedCreative(creative: any): boolean {
  if (!creative) {
    return false;
  }

  const layout = creative.layout || {};
  const layoutTypes = [
    layout.ogLayoutType,
    layout.layoutType,
    layout.detectedCategory,
    creative.layoutType,
    creative.detectedCategory
  ];

  return layoutTypes.indexOf('VAST') > -1;
}

export function requiresVideoPreviewParam(creative: any): boolean {
  return isVastRelatedCreative(creative);
}

export function requiresBlankPagePreview(creative: any): boolean {
  if (!creative) {
    return false;
  }

  if (isVastRelatedCreative(creative)) {
    return true;
  }

  const layout = creative.layout || {};
  const ogLayoutType = layout.ogLayoutType || '';
  const layoutType = layout.layoutType || '';

  // Layouts loaded without ogLayoutType still treat VIDEO/AUDIO as blank-page-only.
  if (!ogLayoutType) {
    return layoutType === 'VIDEO' || layoutType === 'AUDIO';
  }

  return false;
}

export function shouldShowPreviewLanguageSelector(creative: any): boolean {
  return isPoliticalPreviewCreative(creative) && !isVastRelatedCreative(creative);
}

export function normalizePreviewLanguageSelection(selectedLanguage: string): string {
  return selectedLanguage || 'browser';
}

export function hasSpecificPreviewLanguageSelection(selectedLanguage: string): boolean {
  return normalizePreviewLanguageSelection(selectedLanguage) !== 'browser';
}

export function resolvePreviewLanguageCode(selectedLanguage: string, browserNavigator?: Navigator | any): string {
  selectedLanguage = normalizePreviewLanguageSelection(selectedLanguage);

  if (selectedLanguage && selectedLanguage !== 'browser') {
    return selectedLanguage;
  }

  if (browserNavigator && browserNavigator.languages && browserNavigator.languages.length) {
    return browserNavigator.languages[0];
  }

  if (browserNavigator && browserNavigator.language) {
    return browserNavigator.language;
  }

  return 'en';
}
